/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import FiltersSelectComponent from "app/filters/FiltersSelectComponent";
import FiltersNestedSelectComponent from "app/filters/FiltersNestedSelectComponent";
import getFilteredProductList from "lib/filters/products";
import { AiOutlineShop, AiOutlineTag } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { setSummaryFilters } from "../../filters/duck/slices";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    width: "100%",
    gap: "5px",
    padding: "5px 0px",
    fontSize: "inherit",
    fontFamily: "inherit",
    // marginLeft: "35px",
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
  },
  filterContainer: {
    width: "100%",
    flex: "1 1 auto",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  filterLabel: {
    width: "100%",
    flex: "0 0 40px",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
}));

export default function SummaryFilters({ filteredScenariosData }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { summaryFilters } = useSelector((state) => state.filters);
  const catalogData = useSelector((state) => state.catalog);
  const retailersData = useSelector((state) => state.retailers);
  const retailersGroupData = useSelector((state) => state.retailersGroup);
  const [filteredCategories, setFilteredCategories] = useState(null);
  const [filteredRetailers, setFilteredRetailers] = useState(null);
  const [filteredRetailerGroup, setFilteredRetailerGroup] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState(
    catalogData.products
  );
  const [filteredBrands, setFilteredBrands] = useState(catalogData.brands);

  const scenarioSelectedSlice = useSelector(
    (state) => state.scenarios.selectedScenario
  );

  const referenceScenario = useSelector(
    (state) => state.scenarios.referenceScenario
  );

  const viewSlice = useSelector((state) => state.view);

  const handleChange = (filtersObject) => {
    dispatch(setSummaryFilters(filtersObject));
  };

  useEffect(() => {
    let filteredProductCatalog = null;
    if (Object.keys(referenceScenario).length !== 0) {
      // Use a Set to store unique product_ids
      const productIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique lines_ids
      const categoriesIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique retailers_ids
      let retailersIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique retailers_ids
      let retailerGroupIdFromScenarioSelectedSet = new Set();
      // Use a Set to store unique retailers_ids
      const brandsIdFromScenarioSelectedSet = new Set();

      // We use Sets because we are storing uniques and ids can repeat on each estimation
      Object.values(referenceScenario.estimations).forEach((obj) => {
        productIdFromScenarioSelectedSet.add(obj.product_id);
        categoriesIdFromScenarioSelectedSet.add(obj.product.category_id);
        brandsIdFromScenarioSelectedSet.add(obj.product.brand_id);
        if (!filteredScenariosData) {
          retailersIdFromScenarioSelectedSet.add(obj.retailer_id);
          retailerGroupIdFromScenarioSelectedSet.add(
            obj.retailer.retailer_group_id
          );
        }
      });
      // Filter the object based on keys in the Set
      // It returns an object with the product_id as key, and the product object inside it
      filteredProductCatalog = Object.entries(catalogData.products).reduce(
        (result, [key, value]) => {
          if (productIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        },
        {}
      );

      const filteredCategoriesCatalog = Object.entries(
        catalogData.categories
      ).reduce((result, [key, value]) => {
        if (categoriesIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
          // eslint-disable-next-line no-param-reassign
          result[key] = value;
        }
        return result;
      }, {});

      const filteredBrandsCatalog = Object.entries(catalogData.brands).reduce(
        (result, [key, value]) => {
          if (brandsIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        },
        {}
      );

      let filteredRetailersCatalog = {};
      if (filteredScenariosData) {
        // accessing reference scenario from the response
        const filteredRetailerIds = new Set();
        const referenceFilteredEstimations = Object.entries(
          filteredScenariosData
        )[0] || ["", []];
        const referenceFilteredEstimationsArray =
          Object.values(referenceFilteredEstimations[1]) || [];
        referenceFilteredEstimationsArray.forEach(
          ({ retailer_id: retailerId }) => {
            filteredRetailerIds.add(retailerId);
          }
        );
        retailersIdFromScenarioSelectedSet = filteredRetailerIds;
      }

      filteredRetailersCatalog = Object.entries(retailersData).reduce(
        (result, [key, value]) => {
          if (retailersIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        },
        {}
      );

      let filteredRetailerGroupCatalog = {};
      if (filteredScenariosData) {
        // accessing reference scenario from the response
        const filteredRetailerGroupIds = new Set();
        const referenceFilteredEstimations = Object.entries(
          filteredScenariosData
        )[0] || ["", []];
        const referenceFilteredEstimationsArray =
          Object.values(referenceFilteredEstimations[1]) || [];
        referenceFilteredEstimationsArray.forEach(
          ({ retailer_id: retailerId }) => {
            const retailerGroupId =
              retailersData[retailerId]?.retailer_group_id;
            filteredRetailerGroupIds.add(retailerGroupId);
          }
        );
        retailerGroupIdFromScenarioSelectedSet = filteredRetailerGroupIds;
      }
      filteredRetailerGroupCatalog = Object.entries(retailersGroupData).reduce(
        (result, [key, value]) => {
          if (retailerGroupIdFromScenarioSelectedSet.has(parseInt(key, 10))) {
            // eslint-disable-next-line no-param-reassign
            result[key] = value;
          }
          return result;
        },
        {}
      );

      setFilteredCategories(filteredCategoriesCatalog);
      setFilteredRetailers(filteredRetailersCatalog);
      setFilteredRetailerGroup(filteredRetailerGroupCatalog);
      setFilteredBrands(filteredBrandsCatalog);
    }

    const productsForFilters = getFilteredProductList(
      summaryFilters,
      filteredProductCatalog,
      // We should skip products because we want them all (after being filtered by catalog and brand) to appear in the dropdown
      { product: true }
    );

    setFilteredProducts(productsForFilters);
  }, [
    summaryFilters,
    catalogData,
    scenarioSelectedSlice,
    filteredScenariosData,
  ]);

  return (
    <div className={`${classes.root}`}>
      <div className={classes.filterLabel}>
        <span style={{ width: "100%", fontSize: "12px" }}>Filters:</span>
      </div>
      <div className={classes.filterContainer}>
        <FiltersSelectComponent
          inputData={{
            key: "category",
            label: "Category",
            type: "normal",
            data: filteredCategories,
            icon: <AiOutlineTag size={18} />,
          }}
          filters={{ ...summaryFilters, line: undefined }}
          sort="asc"
          handleChange={handleChange}
          small
          view={viewSlice}
        />
      </div>
      <div className={classes.filterContainer}>
        <FiltersSelectComponent
          inputData={{
            key: "brand",
            label: "Brand",
            type: "normal",
            data: filteredBrands,
            icon: <AiOutlineTag size={18} />,
          }}
          filters={{ ...summaryFilters, line: undefined }}
          sort="asc"
          handleChange={handleChange}
          small
          view={viewSlice}
        />
      </div>
      <div className={classes.filterContainer}>
        <FiltersSelectComponent
          inputData={{
            key: "product",
            label: "Product",
            type: "normal",
            data: filteredProducts,
            icon: <AiOutlineTag size={18} />,
          }}
          filters={summaryFilters}
          sort="asc"
          handleChange={handleChange}
          small
          view={viewSlice}
        />{" "}
      </div>
      {/* <div className={classes.filterContainer}>
        <FiltersSelectComponent
          inputData={{
            key: "retailers",
            label: "Retailers",
            type: "normal",
            data: filteredRetailers,
            icon: <AiOutlineShop size={18} />,
          }}
          filters={summaryFilters}
          sort="asc"
          handleChange={handleChange}
          small
          view={viewSlice}
        />
      </div> */}
      <div className={classes.filterContainer}>
        <FiltersNestedSelectComponent
          inputData={{
            key: "retailers",
            label: "Retailers",
            type: "nested",
            icon: <AiOutlineShop size={22} />,
            data: {
              parent: {
                data: filteredRetailerGroup,
                label: "RetailerGroup",
                key: "retailerGroup",
              },
              children: {
                label: "Retailers",
                key: "retailers",
                data: _.groupBy(
                  filteredRetailers,
                  (retailer) => retailer.retailer_group_id
                ),
              },
            },
          }}
          small
          filters={summaryFilters}
          sort="asc"
          isPricingOrTpo={false}
          disabled={false}
          handleChange={handleChange}
          view="summary"
        />
      </div>
    </div>
  );
}
